import axios from 'axios';
import moment from 'moment';
import { createAction } from 'typesafe-actions';

import { mapFeatures } from '../services/mapFeatures';
import { getInternalMowingEndDate, getInternalMowingStartDate } from '../store/selectors';
import { State } from './reducer';

export const toggleSidePanel = createAction('TOGGLE_SIDE_PANEL')();
export const toggleBasemap = createAction('TOGGLE_BASEMAP')();

export const setIdentifiedEntities = createAction('SET_IDENTIFIED_ENTITIES')<any[]>();
export const setSelectedId = createAction('SET_SELECTED_ID')<number>();
export const clearIdentify = createAction('CLEAR_IDENTIFY')();
export const clearSelectedId = createAction('CLEAR_SELECTED_ID')();

export const toggleInternalMowingVisibility = createAction('TOGGLE_INTERNAL_MOWING_VISIBILITY')();
export const toggleInternalMowingSettings = createAction('TOGGLE_INTERNAL_MOWING_SETTINGS')();
export const setInternalMowingStartDate = createAction(
  'SET_INTERNAL_MOWING_START_DATE'
)<Date | null>();
export const setInternalMowingEndDate = createAction('SET_INTERNAL_MOWING_END_DATE')<Date | null>();
export const setInternalMowingFeatures = createAction('SET_INTERNAL_MOWING_FEATURES')<any>();

export const setPublicMowingFeatures = createAction('SET_PUBLIC_MOWING_FEATURES')<any>();

export const toggleSectionalsVisibility = createAction('TOGGLE_SECTIONALS_VISIBILITY')();
export const toggleTobinMapsVisibility = createAction('TOGGLE_TOBIN_MAPS_VISIBILITY')();

export const toggleOwnershipSettings = createAction('TOGGLE_OWNERSHIP_SETTINGS')();
export const toggleOwnershipVisibility = createAction('TOGGLE_OWNERSHIP_VISIBILITY')();
export const toggleOwnershipLayer = createAction('TOGGLE_OWNERSHIP_LAYER')<string>();

export const toggleAbstractsVisibility = createAction('TOGGLE_ABSTRACTS_VISIBILITY')();

export const toggleDitchesSettings = createAction('TOGGLE_DITCHES_SETTINGS')();
export const toggleDitchesVisibility = createAction('TOGGLE_DITCHES_VISIBILITY')();
export const toggleDitchesLayer = createAction('TOGGLE_DITCHES_LAYER')<string>();

export const toggleBeaumontZoningVisibility = createAction('TOGGLE_BEAUMONT_ZONING_VISIBILITY')();
export const toggleCountiesVisibility = createAction('TOGGLE_COUNTIES_VISIBILITY')();
export const toggleSchoolDistrictsVisibility = createAction('TOGGLE_SCHOOL_DISTRICTS_VISIBILITY')();
export const toggleSubdivisionsVisibility = createAction('TOGGLE_SUBDIVISIONS_VISIBILITY')();
export const toggleCommissionerPrecinctsVisibility = createAction(
  'TOGGLE_COMMISSIONER_PRECINCTS_VISIBILITY'
)();
export const toggleDrainageDistrictsVisibility = createAction(
  'TOGGLE_DRAINAGE_DISTRICTS_VISIBILITY'
)();
export const toggleParcelsVisibility = createAction('TOGGLE_PARCELS_VISIBILITY')();
export const toggleSubwatershedsVisibility = createAction('TOGGLE_SUBWATERSHEDS_VISIBILITY')();
export const toggleWatershedsVisibility = createAction('TOGGLE_WATERSHEDS_VISIBILITY')();

export const setSearchLocation = createAction('SET_SEARCH_LOCATION')<{
  latitude: number;
  longitude: number;
} | null>();

export const initMowingMap = (map: 'internal' | 'public') => async (dispatch: any) => {
  const currentMoment = moment().zone('-05:00');
  const toDateFormatted = currentMoment.format('YYYY-MM-DD');
  const baseURL =
    'https://gis.lja.com/arcgis/rest/services/Hosted/DitchFinal_ALL/FeatureServer/0/query?&f=geojson&outSR=4326';
  switch (map) {
    case 'internal':
      dispatch(setInternalMowingFeatures(null));
      dispatch(setInternalMowingEndDate(currentMoment.toDate()));
      const internalFromDate = moment().zone('-05:00').subtract(1, 'month');
      const internalFromDateFormatted = internalFromDate.format('YYYY-MM-DD');
      dispatch(setInternalMowingStartDate(internalFromDate.toDate()));
      const privateFieldsFilter = '&outFields=*';
      try {
        const response = await axios.get(
          `${baseURL}${privateFieldsFilter}&where=wsdatetime > DATE '${internalFromDateFormatted} 00:00:00' AND wsdatetime < DATE '${toDateFormatted} 23:59:59'`
        );
        const features = response.data.features;
        const mappedFeatures = mapFeatures(features);
        const geoJSON = {
          data: { type: 'FeatureCollection', features: mappedFeatures },
          type: 'geojson',
        };
        dispatch(setInternalMowingFeatures(geoJSON));
      } catch (error) {
        // TODO: Handle error
      }
      break;
    case 'public':
      dispatch(setPublicMowingFeatures(null));
      const publicFromDateFormatted = moment()
        .zone('-05:00')
        .subtract(6, 'month')
        .format('YYYY-MM-DD');
      const publicFieldsFilter =
        '&outFields=objectid,wsdatetime_12,wsdatetime,ditchid_domains,ditch_side';
      try {
        const response = await axios.get(
          `${baseURL}${publicFieldsFilter}&where=wsdatetime > DATE '${publicFromDateFormatted} 00:00:00' AND wsdatetime < DATE '${toDateFormatted} 23:59:59'`
        );
        const features = response.data.features;
        const mappedFeatures = mapFeatures(features);
        const geoJSON = {
          data: { type: 'FeatureCollection', features: mappedFeatures },
          type: 'geojson',
        };
        dispatch(setPublicMowingFeatures(geoJSON));
      } catch (error) {
        // TODO: Handle error
      }
  }
};

export const updateInternalMowingStartDate = (newStartDate: Date | null) => async (
  dispatch: any,
  getState: () => State
) => {
  dispatch(setInternalMowingFeatures(null));
  dispatch(setInternalMowingStartDate(newStartDate));
  const currentEndDate = getInternalMowingEndDate(getState());
  const fromDateFormatted = moment(newStartDate).format('YYYY-MM-DD');
  const toDateFormatted = moment(currentEndDate).format('YYYY-MM-DD');
  const baseURL =
    'https://gis.lja.com/arcgis/rest/services/Hosted/DitchFinal_ALL/FeatureServer/0/query?&f=geojson&outSR=4326';
  const fieldsFilter = '&outFields=*';
  try {
    const response = await axios.get(
      `${baseURL}${fieldsFilter}&where=wsdatetime > DATE '${fromDateFormatted} 00:00:00' AND wsdatetime < DATE '${toDateFormatted} 23:59:59'`
    );
    const features = response.data.features;
    const mappedFeatures = mapFeatures(features);
    const geoJSON = {
      data: { type: 'FeatureCollection', features: mappedFeatures },
      type: 'geojson',
    };
    dispatch(setInternalMowingFeatures(geoJSON));
  } catch (error) {
    // TODO: Handle error
  }
};

export const updateInternalMowingEndDate = (newEndDate: Date | null) => async (
  dispatch: any,
  getState: () => State
) => {
  dispatch(setInternalMowingFeatures(null));
  dispatch(setInternalMowingEndDate(newEndDate));
  const currentStartDate = getInternalMowingStartDate(getState());
  const toDateFormatted = moment(newEndDate).format('YYYY-MM-DD');
  const fromDateFormatted = moment(currentStartDate).format('YYYY-MM-DD');
  const baseURL =
    'https://gis.lja.com/arcgis/rest/services/Hosted/DitchFinal_ALL/FeatureServer/0/query?&f=geojson&outSR=4326';
  const fieldsFilter = '&outFields=*';
  try {
    const response = await axios.get(
      `${baseURL}${fieldsFilter}&where=wsdatetime > DATE '${fromDateFormatted} 00:00:00' AND wsdatetime < DATE '${toDateFormatted} 23:59:59'`
    );
    const features = response.data.features;
    const mappedFeatures = mapFeatures(features);
    const geoJSON = {
      data: { type: 'FeatureCollection', features: mappedFeatures },
      type: 'geojson',
    };
    dispatch(setInternalMowingFeatures(geoJSON));
  } catch (error) {
    // TODO: Handle error
  }
};
